import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  StatusPill,
  StatusPillSelect,
  type StatusPillOption,
} from '@/components/ui/status-pill-select';
import { useUpdateVacancyStatus } from '@/hooks/use-recruitment';
import { usePermission } from '@/lib/permissions';
import type { VacancyStatus } from '@/types/recruitment';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  VACANCY_ALLOWED_TRANSITIONS,
  VACANCY_STATUS_CONFIRM_COPY,
  VACANCY_STATUS_LABELS,
  VACANCY_STATUS_MENU_ORDER,
  VACANCY_STATUS_TONE,
  extractVacancyStatusErrorCode,
} from '../lib/vacancy-status';

interface VacancyStatusPillProps {
  vacancyId: string;
  status: VacancyStatus;
}

export function VacancyStatusPill({ vacancyId, status }: VacancyStatusPillProps) {
  const canChangeStatus = usePermission('recruitment.vacancies.status');
  const canApprove = usePermission('recruitment.vacancies.approve');
  const mutation = useUpdateVacancyStatus(vacancyId);
  const [pendingConfirm, setPendingConfirm] = useState<VacancyStatus | null>(null);

  const allowed = VACANCY_ALLOWED_TRANSITIONS[status];
  const interactive = canChangeStatus && allowed.length > 0;

  if (!interactive) {
    return <StatusPill tone={VACANCY_STATUS_TONE[status]} label={VACANCY_STATUS_LABELS[status]} />;
  }

  const options: Array<StatusPillOption<VacancyStatus>> = VACANCY_STATUS_MENU_ORDER.map(
    (target) => {
      const needsApprove = target === 'ACTIVE' && status !== 'ACTIVE' && !canApprove;
      return {
        value: target,
        label: VACANCY_STATUS_LABELS[target],
        disabled: !allowed.includes(target) || needsApprove,
        disabledReason: needsApprove ? 'Requires approval permission' : undefined,
      };
    },
  );

  const commitChange = (target: VacancyStatus) => {
    mutation.mutate(target, {
      onError: (err) => {
        const code = extractVacancyStatusErrorCode(err);
        if (code === 'vacancy.status.approval_required') {
          toast.error("You don't have permission to approve this vacancy");
        } else if (code === 'vacancy.status.invalid_transition') {
          toast.error('This status change is not allowed');
        } else {
          toast.error('Failed to update status');
        }
      },
    });
  };

  const handleChange = (target: VacancyStatus) => {
    if (VACANCY_STATUS_CONFIRM_COPY[target]) {
      setPendingConfirm(target);
      return;
    }
    commitChange(target);
  };

  const confirmCopy = pendingConfirm ? VACANCY_STATUS_CONFIRM_COPY[pendingConfirm] : null;

  return (
    <>
      <StatusPillSelect
        value={status}
        label={VACANCY_STATUS_LABELS[status]}
        tone={VACANCY_STATUS_TONE[status]}
        options={options}
        onChange={handleChange}
        menuClassName="w-52"
      />
      <AlertDialog
        open={pendingConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setPendingConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmCopy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmCopy?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep as is</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingConfirm) commitChange(pendingConfirm);
                setPendingConfirm(null);
              }}
            >
              {confirmCopy?.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
